import { useCallback, useEffect, useState } from 'react';
import { type PcrMetricsResponse, fetchPcrMetrics } from '../../../services/pcrService';
import { queueHomeRequest } from '../homeRequestQueue';

export interface PcrHomeMetricsFilter {
  /** When set, scopes PCR metrics to this WRAP service area id (16, 17, 22, or 25). */
  serviceAreaId?: number;
  /** When set, scopes PCR metrics to this foreman area (community on PCR rows). */
  foremanAreaId?: number;
  /** `YYYY-MM` — scopes pie-chart data to this month (KPI counts are not month-scoped). */
  chartMonth?: string;
}

export function usePcrHomeMetrics(enabled: boolean, filter: PcrHomeMetricsFilter) {
  const [metrics, setMetrics] = useState<PcrMetricsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sid = filter.serviceAreaId;
  const fid = filter.foremanAreaId;
  const chartMonth = filter.chartMonth;

  const load = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const m = await queueHomeRequest(() =>
        fetchPcrMetrics({
          serviceAreaId: sid != null ? String(sid) : undefined,
          foremanAreaId: fid != null ? String(fid) : undefined,
          chartMonth,
        }),
      );
      setMetrics(m);
    } catch (e) {
      setMetrics(null);
      setError(e instanceof Error ? e.message : 'Failed to load PCR metrics');
    } finally {
      setLoading(false);
    }
  }, [enabled, sid, fid, chartMonth]);

  useEffect(() => {
    void load();
  }, [load]);

  return { metrics, loading, error, refetch: load };
}
